import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import type { IBreadcrumbTitles } from 'components/Breadcrumbs';
import type { ISearchOverlay } from 'components/HeaderNavigation/components/MobileNavigation';
import getSearchClient from 'server/repository/algolia/client';
import type { DictionaryItem } from 'service/dictionary/dictionaryService';
import { TrackingService } from 'service/tracking/trackingService';
import TemplateMapper from 'templates/TemplateMapper';
import type { IGlobalMetaData } from 'types/GlobalMetadata';
import type { AVAILABLE_THEMES_TYPE } from '@/styles/constants';

const Breadcrumbs = dynamic(
  () => import('components/Breadcrumbs').then((m: any) => m.Breadcrumbs),
  { ssr: true },
) as any;
const UpArrow = dynamic(() => import('components/UpArrow').then((m: any) => m.UpArrow), {
  ssr: true,
}) as any;

const ClientSlugPage = ({
  breadcrumbsCMSData,
  data,
  globalMetadata,
  searchOverlayData,
  difficultyCopy,
  recipeCopy,
  searchItemLabels,
  themeName,
}: {
  breadcrumbsCMSData: IBreadcrumbTitles;
  data: any;
  globalMetadata: IGlobalMetaData;
  searchOverlayData: ISearchOverlay;
  recipeCopy: DictionaryItem[];
  difficultyCopy: Record<string, string>;
  searchItemLabels: Record<string, string>;
  themeName: AVAILABLE_THEMES_TYPE | null | undefined;
}) => {
  const page = data.genericPages?.[0] || {};
  const [searchOverlayDataExtended, setSearchOverlayDataExtended] = useState(searchOverlayData);
  const router = useRouter();

  useEffect(() => {
    const cg1 = page?.contentGroupingTrackingValue;
    const cg2 = page?.contentGroupingTrackingValues;
    if (cg1) {
      if (cg2) {
        TrackingService.contentGroups({
          cg1: cg1.replace(/_/g, ' '),
          cg2: cg2.replace(/_/g, ' '),
        });
      } else {
        TrackingService.contentGroups({
          cg1: cg1.replace(/_/g, ' '),
        });
      }
    }
  }, []);

  useEffect(() => {
    if (!searchOverlayDataExtended.searchClient) {
      const algoliaClient = getSearchClient();
      setSearchOverlayDataExtended({
        ...searchOverlayDataExtended,
        searchClient: algoliaClient,
      });
    }
  }, [searchOverlayData]);

  const isNavigatingRef = useRef(false);

  // Handle route change and script injection
  useEffect(() => {
    const scriptId = 'suppress-cookie-banner';

    const updateCookieBannerScript = (shouldSuppress: boolean) => {
      let script = document.getElementById(scriptId) as HTMLScriptElement;

      if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.type = 'application/javascript';
        document.body.appendChild(script);
      }

      // Update script content with the cookieBanner value
      script.textContent = `window.UC_UI_SUPPRESS_CMP_DISPLAY = ${shouldSuppress};`;
    };

    const handleRouteChange = () => {
      isNavigatingRef.current = true;
      // Set the script to false on route change
      updateCookieBannerScript(false);
    };

    // Set initial script value based on the inverted page configuration
    updateCookieBannerScript(!page?.cookieBannerRequired);

    // Attach route change handler
    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      // Only reload on real route changes, not HMR remounts — HMR also runs cleanup functions,
      // which would otherwise cause an infinite reload loop in development.
      if (!page?.cookieBannerRequired && isNavigatingRef.current) {
        window.location.reload();
      }
      isNavigatingRef.current = false;
      // Cleanup route change handler
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router.events, page?.cookieBannerRequired]);

  return (
    <>
      {!themeName && (
        <Breadcrumbs isPositionRelative titles={breadcrumbsCMSData} pageName={page.title} />
      )}
      <TemplateMapper
        components={page?.pageComponents}
        copy={page?.dictionary?.dictionaryItems || null}
        recipeCopy={recipeCopy}
        difficultyCopy={difficultyCopy}
        globalMetadata={globalMetadata}
        searchItemLabels={searchItemLabels}
        searchData={searchOverlayDataExtended}
      />
      <UpArrow />
    </>
  );
};

export default ClientSlugPage;
